import { withStyles } from 'vopay';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EventMessageData, useELinxConnect } from '#shared/components/ELinx';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useDialog } from '#shared/providers/Dialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useToast } from '#shared/providers/Toast';
import useFetchBankAccounts from '#shared/hooks/useFetchBankAccounts';
import useFetchClientCreditCards from '#shared/hooks/useFetchClientCreditCards';
import useFetchDefaultBankAccount from '#shared/hooks/useFetchDefaultBankAccount';
import useFetchDefaultPaymentMethod from '#shared/hooks/useFetchDefaultPaymentMethod';
import useSetClientBankAccount from '#shared/hooks/useSetClientBankAccount';
import useSetClientCreditCard from '#shared/hooks/useSetClientCreditCard';
import useSetDefaultPaymentMethod from '#shared/hooks/useSetDefaultPaymentMethod';
import { Button } from '#shared/components/UI';
import AccountListingPanel from './AccountListingPanel';
import PaymentMethodCard from './PaymentMethodCard/PaymentMethodCard';
import AddBillingDialog, { AddBillingSelection } from './AddBillingDialog';
import buildAccountRows, { getAccountRowToken } from './buildAccountRows';
import useAccountAndBilling from './useAccountAndBilling';
import useAccountListing from './useAccountListing';
import useAccountTypeLabels from './useAccountTypeLabels';
import { getAccountHolderLabel, getMaskedAccountLabel } from './accountLabels';
import { CREDIT_CARD_CURRENCY } from './constants';
import { AccountRow, AccountSearchParams, MyAccountType } from './types';
import styles from './BillingPaymentMethods.module.scss';

const cn = withStyles(styles);

/**
 * The account a confirm dialogue names and the save then acts on. Flattened out of the row (or out of
 * the eLinx payload plus the refetched list) at the point the flow starts, so the dialogue copy and the
 * request both read from one value rather than from loose number/type/name/currency state.
 */
type BillingTarget = {
  type: MyAccountType;
  currency: string;
  token: string;
  maskedNumber: string;
  holderName: string;
};

export default function BillingPaymentMethods() {
  const { viewer, account: hostAccount, userPermissions, billingApi } = useAccountAndBilling();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'Shared.BillingPaymentMethods' });
  // The dialogue copy keeps its own key namespace even though the strings are passed from the awaited
  // calls below rather than from a dialogue component.
  const { t: tDialogue } = useTranslation('translations', { keyPrefix: 'Shared.BillingAccountDialogue' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const { t: tErrors } = useTranslation('translations', { keyPrefix: 'Shared.Errors' });
  const { getAccountTypeLabel } = useAccountTypeLabels();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const dialog = useDialog();
  const eLinxConnect = useELinxConnect();

  const { defaultPaymentMethod, fetchDefaultPaymentMethod } = useFetchDefaultPaymentMethod(
    billingApi.fetchDefaultPaymentMethod,
  );

  const { defaultOperationalBankAccount, defaultBillingBankAccount, fetchDefaultBankAccount } =
    useFetchDefaultBankAccount();

  const { setDefaultPaymentMethod } = useSetDefaultPaymentMethod(billingApi.setDefaultPaymentMethod);
  const { setClientBankAccount } = useSetClientBankAccount();
  const { setClientCreditCard } = useSetClientCreditCard();

  const { creditCards, status: creditCardsStatus, fetchCreditCards } = useFetchClientCreditCards();

  const { bankAccounts, status: bankAccountsStatus, fetchBankAccounts } = useFetchBankAccounts();

  // Every collection the page renders from, derived rather than rebuilt into state by an effect.
  // Billing resolves its default flags off the default *billing* bank account plus the account-wide
  // default payment method: a bank row is the default here only when it is both, and a card row only
  // when the account's default method is 'credit' and the card is the default card.
  const accountRows = useMemo(
    () =>
      buildAccountRows({
        bankAccounts,
        creditCards,
        purpose: 'Billing',
        country: hostAccount.country,
        resolveBankDefaults: (bankAccount) => ({
          IsDefault:
            defaultBillingBankAccount.some((obj) => obj.Token === bankAccount.Token) && defaultPaymentMethod === 'bank',
          IsOperationalDefault: defaultOperationalBankAccount.some((obj) => obj.Token === bankAccount.Token),
          IsBillingDefault: defaultBillingBankAccount.some((obj) => obj.Token === bankAccount.Token),
        }),
        resolveCardDefaults: (creditCard) => {
          const isCardDefault = defaultPaymentMethod === 'credit' && creditCard.IsDefault === '1';

          // Cards belong to no Purpose, so there is no operational default for one to be.
          return { IsDefault: isCardDefault, IsOperationalDefault: false, IsBillingDefault: isCardDefault };
        },
      }),
    [
      bankAccounts,
      creditCards,
      hostAccount.country,
      defaultBillingBankAccount,
      defaultOperationalBankAccount,
      defaultPaymentMethod,
    ],
  );

  // The drawer resolves its row against every row on the account, not just the ones the table shows.
  const listing = useAccountListing({ rows: accountRows.purposeRows, detailRows: accountRows.allRows, search });
  const { currency } = listing.queryParams;

  // Accounts the add-billing dialogue offers: the bank accounts that aren't billing accounts yet.
  const selectableBankAccounts = useMemo(
    () => accountRows.otherRows.filter((row) => row.Type === 'Bank Account'),
    [accountRows],
  );

  // A failed fetch leaves the accounts from the last good one in place — and the credit cards, which
  // are fetched separately and succeed on their own — so the two failure shapes get different
  // surfaces. Both are DERIVED from the fetch status here rather than raised from the catch that saw
  // the failure: a catch can only read the state of the render that fired the request, which on mount
  // is always the empty one, so it cannot tell "nothing loaded" from "some of it did".
  //
  // The credit cards count too: a separate fetch that can fail on its own, leaving the rows short.
  const hasFetchFailed = bankAccountsStatus === 'error' || creditCardsStatus === 'error';
  const isShowingLoadError = hasFetchFailed && accountRows.purposeRows.length === 0;
  const isShowingRefreshError = hasFetchFailed && accountRows.purposeRows.length > 0;

  // Adds the account to billing, optionally as the default, and reports whether it went through — the
  // caller decides which success dialogue that earns. The confirm dialogue is already gone by the time
  // this runs (its promise resolved), so a failure has nothing left to close.
  async function setAccountDefault(asDefault: boolean, target: BillingTarget) {
    const isCard = target.type === 'Credit Card';

    blockingLoader.show();

    try {
      if (isCard) {
        await setClientCreditCard({ CreditCardToken: target.token, SetAsDefault: asDefault, Purpose: 'Billing' });
        await refreshCreditCards();
      } else {
        await setClientBankAccount({ Token: target.token, SetAsDefault: asDefault, Purpose: 'Billing' });
        await refreshBankAccounts();
      }

      await refreshDefaultBankAccount(currency);

      if (asDefault) {
        await setDefaultPaymentMethod({ PaymentMethod: isCard ? 'credit' : 'bank' });
        await fetchDefaultPaymentMethod();
      }

      return true;
    } catch (error) {
      // The hook already logged; the call site's job is to tell the user what the backend said.
      const errorMessage =
        error instanceof Error
          ? error.message
          : tErrors('unknown', { defaultValue: 'An unexpected error occurred. Please try again.' });

      toast.error(
        t('saveError', {
          errorMessage,
          defaultValue: "We couldn't save your billing account: {{errorMessage}}",
        }),
      );

      return false;
    } finally {
      blockingLoader.hide();
    }
  }

  // One mount fetch, credit cards included: a separate un-awaited effect for them is an unhandled
  // rejection whenever /credit-card fails, which under Cypress fails the spec outright.
  //
  // Not the bank accounts or their defaults: the submit-search effect below also runs on mount, and
  // its fetch is the same pair of calls with the URL's filters applied. Firing both races them, and
  // an unfiltered win renders the wrong list for a deep link like ?currency=USD.
  useEffect(() => {
    const load = async () => {
      try {
        await Promise.all([fetchDefaultPaymentMethod(), refreshCreditCards()]);
      } catch {
        // The hooks already logged, and the failure is surfaced by the error state / notice below,
        // both derived from the fetch statuses
      }
    };

    load();
  }, []);

  // The refreshes hand back the lists they fetched, not just the hooks' state: a caller that has to
  // act on what it just fetched (the eLinx add flow, below) is still in the render that fired the
  // request, where bankAccounts/creditCards hold the pre-fetch values.
  // Every bank-account fetch on this page goes through here, so a refresh after a save or a delete
  // asks for the same slice the last search did. Fetching unfiltered instead repopulated the table
  // with every currency while the filter chip still showed one.
  function buildBankAccountParams({ currency: forCurrency, searchTerm }: AccountSearchParams) {
    return {
      ClientControlled: true,
      Token: '',
      Timezone: viewer.timezone,
      ...(forCurrency === 'All' ? {} : { Currency: forCurrency }),
      SearchText: searchTerm,
    };
  }

  async function refreshBankAccounts() {
    const res = await fetchBankAccounts(buildBankAccountParams(listing.queryParams));

    return res.BankAccounts;
  }

  // The creditCard entitlement gates every card fetch on this page. It lives here rather than
  // inside useFetchClientCreditCards so #shared/hooks stays feature-agnostic: the flag is
  // Account & Billing feature data, which an api hook has no host-neutral way to reach.
  async function refreshCreditCards() {
    if (!hostAccount.features.creditCard) return [];

    const res = await fetchCreditCards({ ClientControlled: true });

    return res.CreditCards;
  }

  // Timezone and the All-currency convention are identical on every default-bank-account call.
  async function refreshDefaultBankAccount(forCurrency: string) {
    await fetchDefaultBankAccount({
      Timezone: viewer.timezone,
      ...(forCurrency === 'All' ? {} : { Currency: forCurrency }),
    });
  }

  // Page data: the listing renders a skeleton from the fetch status rather than blocking the whole
  // screen. loading-conventions reserves the fullscreen loader for flows the user must not interact
  // through — here that is the saves and the deletes, not reloading a filtered list.
  async function search(searchParams: AccountSearchParams) {
    try {
      await Promise.all([
        fetchBankAccounts(buildBankAccountParams(searchParams)),
        refreshDefaultBankAccount(searchParams.currency),
      ]);
    } catch {
      // Same as the initial load: surfaced by the derived notice, not from here
    }
  }

  useEffect(() => {
    listing.submitSearch(listing.queryParams);
  }, []);

  function toBillingTarget(account: AccountRow): BillingTarget {
    return {
      type: account.Type,
      currency: account.Currency,
      token: getAccountRowToken(account),
      maskedNumber: getMaskedAccountLabel(account),
      holderName: getAccountHolderLabel(account),
    };
  }

  async function confirmChangeDefault(account: AccountRow) {
    const target = toBillingTarget(account);

    const result = await alertDialog.open(
      tDialogue('ChangeDefaultBilling.title', { defaultValue: 'Change Default Billing Account' }),
      tDialogue('ChangeDefaultBilling.subTitle', {
        accountNumber: target.maskedNumber,
        accountType: getAccountTypeLabel(target.type),
        currency: target.currency,
        defaultValue:
          'Please confirm {{accountNumber}} {{accountType}} to be selected as the {{currency}} default billing account.',
      }),
      {
        abortLabel: tButtons('cancel', { defaultValue: 'Cancel' }),
        confirmLabel: tDialogue('ChangeDefaultBilling.confirmLabel', { defaultValue: 'Set As Default' }),
      },
    );

    if (result !== 'confirm') return;

    // No success feedback on this path, deliberately — wiring a toast here is its own decision.
    await setAccountDefault(true, target);
  }

  // The choice dialogue every add flow ends at, reached both from the pick-an-existing-account dialog
  // and straight from eLinx. Its two buttons are one question — add, or add and make it the default —
  // so they are the alert's abort/confirm, and only the X-equivalent 'close' means "never mind".
  async function confirmAddToBilling(target: BillingTarget) {
    const accountType = getAccountTypeLabel(target.type);

    const result = await alertDialog.open(
      tDialogue('AccountAddTypeOfBilling.addBillingAccount', { defaultValue: 'Add Billing Account' }),
      tDialogue('AccountAddTypeOfBilling.confirmation', {
        accountNumber: target.maskedNumber,
        accountType,
        defaultValue: 'Please confirm {{accountNumber}} {{accountType}} to add it to the billing account list.',
      }),
      {
        abortLabel: tDialogue('AccountAddTypeOfBilling.addBillingAccount', { defaultValue: 'Add Billing Account' }),
        confirmLabel: tDialogue('AccountAddTypeOfBilling.addAndSetAsDefault', {
          defaultValue: 'Add and Set as Default',
        }),
        // Both buttons add the account, so the X is the only way to back out of the flow entirely.
        closeButton: true,
      },
    );

    if (result === 'close') return;

    const isSettingAsDefault = result === 'confirm';
    const isSaved = await setAccountDefault(isSettingAsDefault, target);

    if (!isSaved) return;

    if (isSettingAsDefault) {
      await alertDialog.success(
        tDialogue('AccountChangeDefaultBillingSuccess.defaultChanged', {
          defaultValue: 'Default Successfully Changed',
        }),
        tDialogue('AccountChangeDefaultBillingSuccess.successMessage', {
          accountName: target.holderName,
          accountType,
          currency: target.currency,
          defaultValue:
            '{{accountName}} {{accountType}} has been successfully added as your {{currency}} billing default account.',
        }),
        { confirmLabel: tButtons('okay', { defaultValue: 'Okay' }) },
      );

      return;
    }

    await alertDialog.success(
      tDialogue('AccountAddBillingSuccess.accountAdded', { defaultValue: 'Account Successfully Added' }),
      tDialogue('AccountAddBillingSuccess.successMessage', {
        accountName: target.holderName,
        accountType,
        defaultValue: '{{accountName}} {{accountType}} has been successfully added as a billing account.',
      }),
      { confirmLabel: tButtons('okay', { defaultValue: 'Okay' }) },
    );
  }

  // The pick-an-existing-account dialogue is the one that returns a value, so it is a useDialog() call
  // rather than an alert: it resolves either the token the user picked or a request to connect a new
  // account, and closes itself either way — which is what keeps it from sitting behind the eLinx overlay.
  async function startAddBilling() {
    if (hostAccount.features.elinxDirectAdd) {
      await startELinxAdd();

      return;
    }

    const selection = await dialog.open<AddBillingSelection>(
      (dialogProps) => <AddBillingDialog {...dialogProps} selectableAccounts={selectableBankAccounts} />,
      { closeButton: true },
    );

    if (!selection) return;

    if (selection.source === 'connect') {
      await startELinxAdd();

      return;
    }

    const account = accountRows.allRows.find((row) => getAccountRowToken(row) === selection.token);

    if (!account) return;

    await confirmAddToBilling(toBillingTarget(account));
  }

  // Billing opens eLinx for both methods at once (bank only without the credit-card entitlement, e.g.
  // partners), so what the user linked is only knowable from the resolved payload's PaymentMethod —
  // which then decides which list the new account is looked up in.
  async function startELinxAdd() {
    let link: EventMessageData;

    try {
      link = await eLinxConnect.open(
        {
          PaymentSelectionMethod: hostAccount.features.creditCard ? 'bank,credit' : 'bank',
          ClientControlled: true,
          Language: i18n.language,
        },
        hostAccount.country,
        true,
      );
    } catch {
      // open() rejects when the user closes the overlay: a cancel, not a failure — nothing to surface.
      return;
    }

    const isCard = link.PaymentMethod === 'creditcard';

    blockingLoader.show();

    let linkedTarget: BillingTarget | null = null;

    try {
      const [refreshedCards, refreshedBankAccounts] = await Promise.all([refreshCreditCards(), refreshBankAccounts()]);

      const linked = isCard
        ? refreshedCards.find((card) => card.CreditCardToken === link.Token)
        : refreshedBankAccounts.find((bankAccount) => bankAccount.Token === link.Token);

      if (linked) {
        linkedTarget = {
          type: isCard ? 'Credit Card' : 'Bank Account',
          // The card response carries no currency — VoPay only accepts CAD cards — the bank one does.
          currency: 'Currency' in linked ? linked.Currency : CREDIT_CARD_CURRENCY,
          token: link.Token,
          maskedNumber: getMaskedAccountLabel(linked),
          holderName: getAccountHolderLabel(linked),
        };
      }
    } catch {
      // the hooks already logged; the connected account shows up on the next refresh
    } finally {
      blockingLoader.hide();
    }

    // The confirm dialogue names the account, so it only opens once the account is actually back from
    // the API — and outside the try/finally, so it isn't raised behind the blocking loader.
    if (linkedTarget) await confirmAddToBilling(linkedTarget);
  }

  // The branchy bits of the shared listing arrive as props: which default lookup applies, whether
  // credit cards can be set as default, the permission, and the button label.
  function isDefaultRow(row: AccountRow) {
    if (row.Type === 'Credit Card') return row.IsDefault && defaultPaymentMethod === 'credit';

    return defaultBillingBankAccount.some((obj) => obj.Token === row.Token) && defaultPaymentMethod === 'bank';
  }

  // The default-bank-account response carries no bank identifier — DefaultBankAccountList.BankId was
  // a phantom field, never a column and never aliased by the handler, so this card's logo has never
  // rendered. The listing response does carry one, so the logo id comes from the matching row.
  function getBankIdForToken(token: string) {
    return bankAccounts.find((bankAccount) => bankAccount.Token === token)?.BankID;
  }

  return (
    <>
      {/* One flex row, where a <Box jc="sb" flx="row"> wrapped a div whose CSS repeated the same
          layout. The cards are only shown for a bank or credit default: 'vopay' and 'other' are both
          "no bank or card default on this account", and 'other' is a value the DB column allows. */}
      <div className={cn('billingContainer')}>
        <div className={cn('defaultCards')}>
          {defaultPaymentMethod === 'bank' &&
            [...defaultBillingBankAccount]
              .sort((account1, account2) => account1.Currency.localeCompare(account2.Currency))
              .map((account) => (
                <PaymentMethodCard
                  key={account.Token}
                  type="bank"
                  title={t('Card.billingDefault', {
                    currency: account.Currency,
                    defaultValue: '{{currency}} Billing Default',
                  })}
                  description={t('Card.billingDescription', {
                    currency: account.Currency,
                    defaultValue: 'Account that is paying the {{currency}} monthly fees on your invoice',
                  })}
                  accountNumber={account.AccountNumber}
                  accountHolder={account.AccountHolderName}
                  connectionType={account.Type === 'manual' ? 'manual' : 'online'}
                  bankId={getBankIdForToken(account.Token)}
                />
              ))}

          {defaultPaymentMethod === 'credit' &&
            creditCards
              .filter((creditCard) => creditCard.IsDefault === '1')
              .map((creditCard) => (
                <PaymentMethodCard
                  key={creditCard.CreditCardToken}
                  type="credit"
                  title={t('Card.billingDefault', {
                    currency: CREDIT_CARD_CURRENCY,
                    defaultValue: '{{currency}} Billing Default',
                  })}
                  description={t('Card.billingDescription', {
                    currency: CREDIT_CARD_CURRENCY,
                    defaultValue: 'Account that is paying the {{currency}} monthly fees on your invoice',
                  })}
                  accountNumber={creditCard.CreditCardNumber}
                  accountHolder={creditCard.CreditCardHolderName}
                  expiry={`20${creditCard.CreditCardExpiryYear}/${creditCard.CreditCardExpiryMonth}`}
                  creditCardBrand={creditCard.CreditCardBrand}
                />
              ))}
        </div>

        <Button
          id="addBillingMethod"
          colorScheme="primary"
          disabled={!userPermissions.canManageBilling}
          onClick={startAddBilling}
        >
          {t('Button.addBillingMethod', { defaultValue: 'Add Billing Method' })}
        </Button>
      </div>

      <AccountListingPanel
        listing={listing}
        accountListingType="Billing"
        canSetDefault={userPermissions.canManageBilling}
        canDelete={userPermissions.canManageBilling}
        canSetDefaultCreditCards
        setDefaultLabel={t('Table.setAsDefaultBilling', { defaultValue: 'Set as Default Billing' })}
        isDefaultRow={isDefaultRow}
        onSetDefault={confirmChangeDefault}
        isLoading={bankAccountsStatus === 'idle' || bankAccountsStatus === 'loading' || creditCardsStatus === 'loading'}
        refreshError={
          isShowingRefreshError
            ? t('refreshError', {
                defaultValue: "We couldn't refresh your payment methods. The list below may be out of date.",
              })
            : undefined
        }
        loadError={
          isShowingLoadError
            ? t('loadError', { defaultValue: "We couldn't load your payment methods. Please try again." })
            : undefined
        }
        onAccountDeleted={async () => {
          blockingLoader.show();

          try {
            if (listing.selectedAccount?.Type === 'Credit Card') {
              await refreshCreditCards();
            } else {
              await refreshBankAccounts();
            }
          } catch {
            // the hooks already logged; the deleted row disappears on the next refresh
          } finally {
            blockingLoader.hide();
          }
        }}
      />
    </>
  );
}
